// Tabla de productos del inventario
import React, { useMemo } from 'react';
import { Button } from '../UI/Index';
import { useAuth } from '../../context/AuthContext';

const style = `
@keyframes blinkRedInfinite {
  0%, 100% { background-color: #ffe5e5; }
  50% { background-color: #ffb3b3; }
}

.blink-red {
  animation: blinkRedInfinite 1.2s ease-in-out infinite;
}
`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getAdjustmentStatusLabel = (status) => {
  switch (status) {
    case 'NotApplicable':
      return 'Sin inventariar';
    case 'Synchronized':
      return 'Sincronizado';
    case 'SyncError':
      return 'Error de sincronización';
    default:
      return 'Sin inventariar';
  }
};

const getAdjustmentStatusColor = (status) => {
  switch (status) {
    case 'NotApplicable':
      return 'bg-gray-100 text-gray-800';
    case 'Synchronized':
      return 'bg-green-100 text-green-800';
    case 'SyncError':
      return 'bg-red-100 text-red-800';
    case 'RequiresValidation':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};




const InventoryDetailsList = ({ details, onUpdateDetail, isCompleted, isAdmin }) => {
  const { roles } = useAuth();

  const sortedDetails = useMemo(() => {
    if (roles.isAdmin || roles.isManager) {
      return [...details].sort((a, b) => {

        if (a.is_sensitive === 1 && b.is_sensitive !== 1) return -1;
        if (a.is_sensitive !== 1 && b.is_sensitive === 1) return 1;

        if (a.requires_validation === 1 && b.requires_validation !== 1) return -1;
        if (a.requires_validation !== 1 && b.requires_validation === 1) return 1;
        return 0;
      });
    }
    return details;
  }, [details, roles.isAdmin, roles.isManager]);

  const shouldShowButton = (detail) => {
    if (isCompleted) return false;

    const canValidate = roles.isManager || roles.isAdmin;

    const notInventoried = detail.qty_input_picked === null && detail.qty_picked_validation === null;

    const needsValidation = canValidate &&
      detail.requires_validation === 1 &&
      detail.validated_at === null;

    return notInventoried || needsValidation;
  };


  const shouldDisableButton = (detail) => {
    return detail.requires_validation === 1 && detail.validated_at !== null;
  };

  const getRowHighlightClass = (detail) => {
    const canValidate = roles.isManager || roles.isAdmin;
    const requiresValidation = detail.requires_validation === 1;
    const notYetValidated = detail.validated_at === null || detail.validated_at === undefined;
    const isSensitive = detail.is_sensitive === 1;
    const isCompleted = detail.status === "Synchronized"; 

    if ((canValidate && requiresValidation && notYetValidated) || (isSensitive)) {
      return 'blink-red';
    }
    return '';
  };

  return (
    <>
      <style>{style}</style>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-1 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">

              </th>
              <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Cod.Prod.
              </th>
              <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Nombre prod.
              </th>
              <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Zona
              </th>
              <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Status
              </th>
              <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Cantidad Ingresada
              </th>

              {isAdmin === 1 && (
                <>
                  <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Cantidad Polarik
                  </th>
                  <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Cantidad Final
                  </th>
                  <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Sincronización Polarik
                  </th>
                  <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Requiere Validación
                  </th>
                  <th scope="col" className="px-2 py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Producto Sensible
                  </th>
                </>
              )}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {sortedDetails.map((detail) => (
              <tr
                key={detail.inventory_detail_id}
                className={`hover:bg-gray-50 ${getRowHighlightClass(detail)}`}
              >
                <td className="px-1 text-sm text-left whitespace-nowrap">
                  {shouldShowButton(detail) && (
                    <Button
                      type="primary"
                      className="text-sm"
                      onClick={() => onUpdateDetail(detail)}
                      disabled={shouldDisableButton(detail)}
                    >
                      +
                    </Button>
                  )}
                </td>

                <td className="px-2 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{detail.product_code}</div>
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                      {detail.product_name}
                  </div>
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{detail.zone}</div>
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{detail.status}</div>
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {detail.qty_input_picked ?? '-'}
                  </div>
                </td>

                {isAdmin === 1 && (
                  <>
                    <td className="px-1 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {detail.qty_pos_returned ?? '-'}
                      </div>
                    </td>
                    <td className="px-1 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {detail.final_qty ?? '-'}
                      </div>
                    </td>
                    <td className="px-1 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-1 py-1 text-xs font-semibold rounded-full ${getAdjustmentStatusColor(detail.pos_adjustment_status)}`}>
                        {getAdjustmentStatusLabel(detail.pos_adjustment_status)}
                      </span>
                    </td>
                    <td className="px-1 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {detail.requires_validation === 1 ? 'Sí' : 'No'}
                      </div>
                    </td>
                                        <td className="px-1 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {detail.is_sensitive === 1 ? 'Sí' : 'No'}
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default InventoryDetailsList;